<template>
  <teleport to="body">
    <div class="toast-stack" aria-live="polite" aria-atomic="false">
      <transition-group name="toast" tag="div" class="toast-stack__inner">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-item"
          :class="`toast-item--${toast.type}`"
          role="alert"
        >
          <div class="toast-item__icon">
            <svg v-if="toast.type === 'success'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
            <svg v-else-if="toast.type === 'error'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <svg v-else-if="toast.type === 'warning'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
          </div>

          <div class="toast-item__body">
            <p class="toast-item__title">{{ toast.title }}</p>
            <p v-if="toast.messages.length === 1" class="toast-item__msg">{{ toast.messages[0] }}</p>
            <ul v-else class="toast-item__list">
              <li v-for="(m, i) in toast.messages" :key="i">{{ m }}</li>
            </ul>
          </div>

          <button class="toast-item__close" @click="dismiss(toast.id)" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div class="toast-item__progress">
            <div
              class="toast-item__progress-bar"
              :style="{ animationDuration: `${toast.duration ?? DURATION}ms` }"
            />
          </div>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script>
let _id = 0

const DURATION = 3000
const ERROR_DURATION = 8000

export default {
  name: 'FlashMessages',

  data() {
    return {
      toasts: [],
      DURATION,
    }
  },

  watch: {
    '$page.props.flash': {
      handler(flash) {
        if (flash?.success) this.push('success', flash.success)
        if (flash?.error)   this.push('error',   flash.error)
      },
      deep: true,
    },
    '$page.props.errors': {
      handler(errors) {
        if (!errors || !Object.keys(errors).length) return
        const msgs = Object.values(errors)
        this.push('error', msgs.length === 1 ? msgs[0] : msgs, msgs.length > 1)
      },
      deep: true,
    },
  },

  methods: {
    push(type, message, isMulti = false) {
      const id = ++_id
      const messages = Array.isArray(message) ? message : [message]
      const titles = { success: 'Success', error: 'Error', warning: 'Warning', info: 'Info' }
      const duration = (type === 'error' || type === 'warning') ? ERROR_DURATION : DURATION

      this.toasts.push({ id, type, title: titles[type], messages, duration })

      setTimeout(() => this.dismiss(id), duration)
    },

    dismiss(id) {
      this.toasts = this.toasts.filter(t => t.id !== id)
    },
  },
}
</script>

<style scoped>
.toast-stack {
  position: fixed;
  top: 1.25rem;
  right: 1.25rem;
  z-index: 9999;
  pointer-events: none;
  width: 360px;
  max-width: calc(100vw - 2rem);
}

.toast-stack__inner {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.toast-item {
  pointer-events: all;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid transparent;
  position: relative;
  overflow: hidden;
}

.toast-item--success { border-left-color: #22c55e; }
.toast-item--error   { border-left-color: #ef4444; }
.toast-item--warning { border-left-color: #f59e0b; }
.toast-item--info    { border-left-color: #6366f1; }

.toast-item__icon {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.05rem;
}
.toast-item__icon svg { width: 1rem; height: 1rem; }

.toast-item--success .toast-item__icon { background: #dcfce7; color: #16a34a; }
.toast-item--error   .toast-item__icon { background: #fee2e2; color: #dc2626; }
.toast-item--warning .toast-item__icon { background: #fef3c7; color: #d97706; }
.toast-item--info    .toast-item__icon { background: #e0e7ff; color: #4f46e5; }

.toast-item__body {
  flex: 1;
  min-width: 0;
}

.toast-item__title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.15rem;
  line-height: 1.3;
}

.toast-item__msg {
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.45;
}

.toast-item__list {
  font-size: 0.8125rem;
  color: #475569;
  padding-left: 1rem;
  margin: 0;
  line-height: 1.6;
}

.toast-item__close {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  margin-top: 0.05rem;
}
.toast-item__close:hover { background: #f1f5f9; color: #475569; }
.toast-item__close svg { width: 0.875rem; height: 0.875rem; }

.toast-item__progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
}

.toast-item__progress-bar {
  height: 100%;
  width: 100%;
  transform-origin: left;
  animation: toast-shrink linear forwards;
}

.toast-item--success .toast-item__progress-bar { background: #22c55e; }
.toast-item--error   .toast-item__progress-bar { background: #ef4444; }
.toast-item--warning .toast-item__progress-bar { background: #f59e0b; }
.toast-item--info    .toast-item__progress-bar { background: #6366f1; }

@keyframes toast-shrink {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

.toast-enter-active {
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.toast-leave-active {
  transition: all 0.25s ease-in;
}
.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Dark mode */
:global([data-mode="dark"]) .toast-item,
:global(.dark) .toast-item {
  background: #1e293b;
  box-shadow: 0 8px 30px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3);
}
:global([data-mode="dark"]) .toast-item__title,
:global(.dark) .toast-item__title {
  color: #f1f5f9;
}
:global([data-mode="dark"]) .toast-item__msg,
:global([data-mode="dark"]) .toast-item__list,
:global(.dark) .toast-item__msg,
:global(.dark) .toast-item__list {
  color: #94a3b8;
}
:global([data-mode="dark"]) .toast-item__close:hover,
:global(.dark) .toast-item__close:hover {
  background: #334155;
  color: #e2e8f0;
}
</style>
